import { rememberAuthUser, settlePendingServerLogoutBeforeAuthentication, type AuthUser } from "./authService";
import { currentDeviceName, getDeviceId } from "./deviceService";

export interface PasskeyInfo {
  id: number;
  name: string;
  device_id: string | null;
  created_at: string;
  last_used_at: string | null;
}

type OptionsEnvelope = { challenge_id: string; publicKey: Record<string, unknown> };

function decodeBase64Url(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes.buffer;
}

function encodeBase64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

type CredentialDescriptorJson = Omit<PublicKeyCredentialDescriptor, "id"> & { id: string };
type UserEntityJson = Omit<PublicKeyCredentialUserEntity, "id"> & { id: string };
type RegistrationOptionsJson = Omit<PublicKeyCredentialCreationOptions, "challenge" | "user" | "excludeCredentials"> & {
  challenge: string;
  user: UserEntityJson;
  excludeCredentials?: CredentialDescriptorJson[];
};
type AuthenticationOptionsJson = Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials"> & {
  challenge: string;
  allowCredentials?: CredentialDescriptorJson[];
};

function registrationOptions(raw: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const value = structuredClone(raw) as unknown as RegistrationOptionsJson;
  return {
    ...value,
    challenge: decodeBase64Url(value.challenge),
    user: { ...value.user, id: decodeBase64Url(value.user.id) },
    excludeCredentials: value.excludeCredentials?.map((item) => ({ ...item, id: decodeBase64Url(item.id) })),
  };
}

function authenticationOptions(raw: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const value = structuredClone(raw) as unknown as AuthenticationOptionsJson;
  return {
    ...value,
    challenge: decodeBase64Url(value.challenge),
    allowCredentials: value.allowCredentials?.map((item) => ({ ...item, id: decodeBase64Url(item.id) })),
  };
}

function serializeRegistration(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: encodeBase64Url(response.clientDataJSON),
      attestationObject: encodeBase64Url(response.attestationObject),
      transports: response.getTransports?.() ?? [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  };
}

function serializeAuthentication(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: encodeBase64Url(response.clientDataJSON),
      authenticatorData: encodeBase64Url(response.authenticatorData),
      signature: encodeBase64Url(response.signature),
      userHandle: response.userHandle ? encodeBase64Url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  };
}

export function passkeysSupported(): boolean {
  return Boolean(window.PublicKeyCredential && navigator.credentials);
}

export async function fetchPasskeys(): Promise<PasskeyInfo[]> {
  const response = await fetch("/api/passkeys", { credentials: "include", cache: "no-store" });
  if (!response.ok) throw new Error("Passkeys konnten nicht geladen werden.");
  return response.json() as Promise<PasskeyInfo[]>;
}

export async function registerPasskey(name = currentDeviceName()): Promise<void> {
  if (!passkeysSupported()) throw new Error("Passkeys werden von diesem Browser nicht unterstützt.");
  const optionsResponse = await fetch("/api/passkeys/register/options", { method: "POST", credentials: "include" });
  if (!optionsResponse.ok) throw new Error("Passkey-Registrierung konnte nicht gestartet werden.");
  const envelope = await optionsResponse.json() as OptionsEnvelope;
  const credential = await navigator.credentials.create({ publicKey: registrationOptions(envelope.publicKey) });
  if (!(credential instanceof PublicKeyCredential)) throw new Error("Passkey-Registrierung wurde abgebrochen.");
  const finish = await fetch("/api/passkeys/register/finish", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: envelope.challenge_id, credential: serializeRegistration(credential), name, device_id: getDeviceId() }),
  });
  if (!finish.ok) {
    const data = await finish.json().catch(() => ({})) as { detail?: string };
    throw new Error(data.detail || "Passkey konnte nicht gespeichert werden.");
  }
}

export async function deletePasskey(id: number): Promise<void> {
  const response = await fetch(`/api/passkeys/${id}`, { method: "DELETE", credentials: "include" });
  if (!response.ok) throw new Error("Passkey konnte nicht entfernt werden.");
}

export async function loginWithPasskey(): Promise<AuthUser> {
  if (!passkeysSupported()) throw new Error("Passkeys werden von diesem Browser nicht unterstützt.");
  await settlePendingServerLogoutBeforeAuthentication();
  const optionsResponse = await fetch("/api/passkeys/authenticate/options", { method: "POST", credentials: "include" });
  if (!optionsResponse.ok) throw new Error("Passkey-Anmeldung konnte nicht gestartet werden.");
  const envelope = await optionsResponse.json() as OptionsEnvelope;
  const credential = await navigator.credentials.get({ publicKey: authenticationOptions(envelope.publicKey) });
  if (!(credential instanceof PublicKeyCredential)) throw new Error("Passkey-Anmeldung wurde abgebrochen.");
  const finish = await fetch("/api/passkeys/authenticate/finish", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: envelope.challenge_id, credential: serializeAuthentication(credential) }),
  });
  if (!finish.ok) {
    const data = await finish.json().catch(() => ({})) as { detail?: string };
    throw new Error(data.detail || "Passkey-Anmeldung fehlgeschlagen.");
  }
  return rememberAuthUser(await finish.json() as AuthUser);
}


export async function confirmAdminRoleChangeWithPasskey(): Promise<string> {
  if (!passkeysSupported()) throw new Error("Passkeys werden von diesem Browser nicht unterstützt.");
  const optionsResponse = await fetch("/api/passkeys/step-up/options", { method: "POST", credentials: "include" });
  if (!optionsResponse.ok) {
    const data = await optionsResponse.json().catch(() => ({})) as { detail?: string };
    throw new Error(data.detail || "Passkey-Bestätigung konnte nicht gestartet werden.");
  }
  const envelope = await optionsResponse.json() as OptionsEnvelope;
  const credential = await navigator.credentials.get({ publicKey: authenticationOptions(envelope.publicKey) });
  if (!(credential instanceof PublicKeyCredential)) throw new Error("Passkey-Bestätigung wurde abgebrochen.");
  const finish = await fetch("/api/passkeys/step-up/finish", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: envelope.challenge_id, credential: serializeAuthentication(credential) }),
  });
  const data = await finish.json().catch(() => ({})) as { detail?: string; step_up_token?: string };
  if (!finish.ok || !data.step_up_token) throw new Error(data.detail || "Passkey-Bestätigung fehlgeschlagen.");
  return data.step_up_token;
}
